import { Button, DialogContent, Stack, Typography, DialogTitle, useTheme, useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import { Markdown } from "@/app/features/shared/markdown/Markdown";
import Dialog from "@mui/material/Dialog";
import FormBuilder from "@/app/features/shared/form-builder/ui/FormBuilder";
import { FormField, FormState } from "@/app/features/shared/form-builder/types/types";
import { defaultFormState } from "@/app/features/shared/form-builder/types/defaults";
import { UnregisterNewsletterFormData } from "@/app/features/sites/header/ui/desktop/flyout-info/flyouts/types/newsletter/form-data";
import { sendEditors } from "@/app/lib/actions/sendEditors";
import DialogActions from "@mui/material/DialogActions";
import { UnregisterNewsletterFormSchema } from "@/app/features/sites/header/ui/desktop/flyout-info/flyouts/types/newsletter/form-schema";
import useIsDesktop from "@/hooks/useDesktop";
import { signatureBorderStyle } from "@/app/theme/theme-tokens";

const preText = `
Hiermit teilen Sie uns mit dass Sie keine weiteren Newsletter erhalten möchten. 
Sobald diese Anfrage bearbeitet worden ist sind Ihre Daten vollständig aus unserem Verteiler entfernt.
`;


const unregisterFields: FormField[] = [
    {
        type: "text",
        name: "email",
        label: "E-Mail-Adresse",
        required: true,
    },
    {
        type: "text",
        name: "reason",
        label: "Grund (optional)",
    }
]

const defaultFormData: UnregisterNewsletterFormData = {
    email: "",
    reason: "",
}

export default function NewsletterUnregisterDialog() {

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);

    const [formData, setFormData] = useState<UnregisterNewsletterFormData>(defaultFormData)
    const [formState, setFormState] = useState<FormState>(defaultFormState)

    const isDesktop = useIsDesktop();

    useEffect(() => {
        if (!isModalOpen) {
            setFormData(defaultFormData)
            setFormState(defaultFormState)
        }
    }, [isModalOpen]);

    useEffect(() => {
        const validated = UnregisterNewsletterFormSchema.safeParse(formData);

        if (!validated.success) {
            setFormState((prev: FormState) => ({
                ...prev,
                fieldErrors: validated.error.formErrors.fieldErrors,
                isValid: false
            }));
        } else {
            setFormState((prev: FormState) => ({
                ...prev,
                fieldErrors: null,
                isValid: true
            }));
        }

    }, [formData, setFormState]);

    const onStart = () => {
        setIsModalOpen(true);
    }

    const onFormSubmit = async () => {
        setFormState((prev: FormState) => ({
            ...prev,
            isSubmitting: true
        }));
        const message = [
            '## Abmeldung vom Newsletter',
            '',
            'Der Besucher hat darum gebeten ihn aus dem Newsletterverteiler zu entfernen.',
            '',
            '### Formulardaten:',
            `- **E-Mail:** ${formData.email}`,
            `- **Grund:** ${formData.reason || 'n/a'}`,
        ].join('\r\n'); // normalize to CRLF for better email client compatibility

        try {
            await sendEditors({
                subject: "🚫 Abmeldung vom Newsletter",
                email: "peter.c.glade@googlemail.com",
                message
            });

            setFormState({
                ...formState,
                success: true
            })

            setIsSuccessDialogOpen(true);
        } catch (error) {
            console.error('Error sending editors mail: ', JSON.stringify(error))
            const errorText = "Das hat leider nicht geklappt, bitte versuchen Sie es später noch einmal oder melde sich direkt unter museum@erding.de";
            setFormState({
                ...formState,
                error: errorText,
                success: false
            })

        } finally {
            setFormState((prev: FormState) => ({
                ...prev,
                isSubmitting: false
            }));
        }
        setIsModalOpen(false);
    }

    const resetForm = () => {
        setFormData(defaultFormData)
        setFormState(defaultFormState)
        setIsModalOpen(false);
        setIsSuccessDialogOpen(false);
    }

    return (
        <>
            <Typography
                variant="body1"
                color="text.primary"
                sx={{
                    cursor: 'pointer',
                    width: 'fit-content',
                }}
                textAlign="right"
                alignSelf="flex-end"
                onClick={onStart}
            >
                ...oder hier abmelden
            </Typography>

            <Dialog
                open={isModalOpen}
                onClose={resetForm}
                fullScreen={!isDesktop}
                fullWidth
                maxWidth="sm"
                scroll="paper"
                sx={{
                    ...(isDesktop ? signatureBorderStyle : {})
                }}
                PaperProps={{
                    sx: {
                        mx: isDesktop ? 0 : { xs: 1, sm: 0 }
                    }
                }}
            >
                <DialogTitle>
                    <Typography
                        variant="h2"
                        component="span"
                        color="text.secondary"
                        className="px-6 pt-6 text-center"
                    >
                        Abmeldung vom Newsletter
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Markdown  value={preText}/>
                        <FormBuilder
                            key={isModalOpen ? 'open' : 'closed'}
                            purpose="send"
                            fields={unregisterFields}
                            formState={formState}
                            formData={formData}
                            // @ts-expect-error ignore any
                            setFormData={(data) => setFormData((prev) => ({ ...prev, ...data }))}
                            onSubmit={() => onFormSubmit()}
                            onCancel={resetForm}
                            disableSaveAndClose
                        />
                    </Stack>
                </DialogContent>
            </Dialog>
            <SuccessDialog isOpen={isSuccessDialogOpen} onApprove={resetForm} />
        </>
    );
}


function SuccessDialog({ isOpen = false, onApprove }: { isOpen: boolean, onApprove: () => void }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
    return (
        <Dialog open={isOpen} fullScreen={fullScreen} fullWidth maxWidth="sm" scroll="paper" PaperProps={{ sx: { mx: fullScreen ? 0 : { xs: 1, sm: 0 } } }}>
            <DialogTitle>
                <Typography
                    variant="h1"
                    color="text.secondary"
                    className="px-6 pt-6 text-center"
                >
                    Erfolgreich abgemeldet! 🎉
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Markdown
                    
                    value={`              
Schade dass sie unserem Newsletter nicht mehr folgen. 

Bitte beachten Sie dass Sie u.U noch den Newsletter erhalten solange ihre Anfrage nicht bearbietet wurde. Wir werden Sie so schnell wie möglich aus underem Newsletterverteiler entfernen. 

**Wenn Sie Anregungen oder Fragen haben wenden Sie sich gerne direkt an uns:** [museum@erding.de](mailto:museum@erding.de)
                    `}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    onClick={onApprove}
                >
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    )
}
